/**
 * QuickConditions — 고른 줄 조건의 빠른 편집: 매스컨셉 · 라벨 · 난이도(설계 FE-20260912 §W01·§W02·§W04).
 *
 * 식은 상세 편집(QueryEditor)과 **같은 모델**이다(quickExpr). 빠른 편집은 언제나 **고른 묶음 하나**만 고친다 —
 * 묶음이 둘 이상인 식을 「난이도 3·5」 하나로 평탄화하지 않는다. 같은 축의 항이 여러 개면 축마다 고칠 항을 선택한다.
 * 다른 항(출처·정답 있음 …)은 글자 칩으로 보이고, 누르면 상세 조건으로 보낸다.
 *
 * 매스컨셉은 전체 목록 창(ConceptListModal, 설계 FE-20260913 §2-1)에서 고른다 — 누르거나 끌어 훑은 개념이 손을 뗀 순간
 * onChange 로 들어간다. 창은 **연 순간의 식** 위에서 그 항 하나만 고친다(값을 다 풀어 항이 빠졌다 다시 골라도 같은 자리에 선다).
 * 창을 닫을 때 바뀐 것이 있으면 onApplySearch 를 한 번 부른다 — 강의 작업 칸은 초안만 다시 넣고(검색은 0.8초 자동 검색),
 * 시험지 조립(W07)은 거기서 검색한다. 라벨은 판(LabelPopover)의 [적용]이 onApplySearch 를 부른다.
 * 매스컨셉·라벨 줄 끝의 방식(하나라도·모두·정확히)은 고치는 항의 방식만 바꾼다(onChange) — 항이 없으면 방식을 둘 곳이 없어 잠근다.
 * 개념 범위 조건(개념 「제외」 · 범위에서 펼친 「하나라도」)은 매스컨셉 줄이 고치지 않는다 — 「그 밖」 칩으로 보이고 상세 조건에서 고친다
 * (오너 2026-09-15 「제외 기능은 범위 기능으로 별도로」).
 * 개념 목록 창에는 방식을 두지 않는다(오너 2026-09-13 「이 조건설정은 우측 끝으로」). 라벨 판의 방식 칸은 그대로 둔다.
 * 난이도 켜고 끄기·칩 빼기는 onChange 로 초안만 바꾼다(설계 FE-20260913 §5). 난이도는 축 고르기를 거치지 않고 누르면 항이 생긴다(수용 기준 3).
 * 「제한 없음」은 난이도 항을 없애는 것이고, 1~5 를 다 켠 것과 다르다(다 켜면 「난이도가 매겨진 문제만」).
 *
 * disabled 는 **고치는 손잡이**만 잠근다. [상세 조건]과 다른 조건 칩은 보러 가는 길이라 열어 둔다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import MathName from './MathName';
import { Check, ChevronDown, Plus, SlidersHorizontal, X } from 'lucide-react';
import { ConceptChip } from '../common';
import { difficultyBadgeClass } from '../ProblemHubV2/problemApi';
import ConceptListModal from './ConceptListModal';
import LabelPopover, { LABEL_MATCH_HINT } from './LabelPopover';
import { labelNameOf } from './LabelPicker';
import { makeNames, unknownLabelText } from './names';
import {
  FIELDS, MATCH_LABEL, describeTerm, pickableMatches,
} from './syllabusQuery';
import { CONCEPT_MATCH_HINT } from './conceptPick';
import {
  QUICK_FIELDS, groupCount, clampGroup, groupName, termIndexes, quickTerm, quickValues,
  otherTermIndexes, setFieldValues, setFieldMatch, toggleDifficulty, clearDifficulty, summarizeGroup, newExpr,
} from './quickExpr';
import { exprKey, sameJson } from './workspaceModel';

/** 조건이 없는 줄(제목 줄)이 여는 빈 묶음 하나 — 고르면 quickExpr 가 새 식을 돌려주므로 이 값은 고치지 않는다 */
const EMPTY_QUERY = newExpr();

/** 개념 칩을 이만큼만 펼치고 나머지는 「외 N개」 — 조건 영역이 결과를 밀어내지 않게(설계 §W01 「높이를 제한」) */
export const CHIP_MAX = 3;

/** 화면에 적는 줄 이름 — 식 모델의 축 이름(FIELDS.label)과 따로 둔다. 개념 축은 이 줄에서만 「매스컨셉」으로 부른다 */
const ROW_NAME = { concept: '매스컨셉', label: '라벨' };
const MATCH_HINT = { concept: CONCEPT_MATCH_HINT, label: LABEL_MATCH_HINT };

const ring = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary';
const pillCls = (on) => `rounded-full border px-2 py-0.5 text-xs transition-colors ${ring} ${
  on ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
}`;
const addCls = `inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 ${ring}`;
const handleCls = `shrink-0 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 ${ring}`;
const linkCls = `inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-gray-600 transition-colors hover:bg-gray-100 ${ring}`;
const rowLabelCls = 'w-14 shrink-0 pt-1 text-xs text-gray-500';
// 축(매스컨셉·라벨·난이도·그 밖)마다 카드 한 장 — 한 줄씩 붙어 있으면 어디까지가 어느 축인지 흐려진다(오너 2026-09-14).
// 얼굴은 상세 편집기(QueryEditor)의 묶음 카드와 같다 — 같은 식을 고치는 두 화면이 다른 카드를 쓰지 않게
const rowCls = 'flex items-start gap-2 rounded-xl border border-gray-200 p-2';
// 잠김은 흐리게도 보인다 — 같은 줄의 다른 손잡이(addCls·handleCls)와 같게. 안 그러면 눌리지 않는 버튼이 켜진 것처럼 보인다
const diffCls = (on) => `inline-flex size-7 items-center justify-center rounded-full text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${ring} ${
  on ? difficultyBadgeClass(on) : 'border border-gray-200 bg-white text-gray-400 hover:bg-gray-50'
}`;

/** 「공통」 꼬리표 — 이 행의 항이 상위 행의 지금 저장 조건과 똑같다(설계 FE-20260913 §4 규칙 8). 이 행에서 바꾸면 사라진다 */
function CommonTag({ from }) {
  return (
    <span
      data-common-tag=""
      title={`상위 「${from}」의 공통 조건 — 이 행에서 따로 바꿀 수 있습니다`}
      className="inline-flex shrink-0 items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary"
    >
      공통
    </span>
  );
}

function QuickConditions({
  query: rawQuery,
  onChange,
  onApplySearch,
  groupIndex = 0,
  onGroupChange,
  dict = null,
  labelNames = {},
  onRememberLabels = null,
  onOpenDetail,
  // [상세 조건] 버튼을 여닫기로(오너 2026-09-14) — 주면 버튼은 이것을 부르고 detailOpen 으로 켜짐을 보인다.
  // 「그 밖」 칩·중복 경고의 [상세 조건] 링크는 「고치러 가기」라 여전히 onOpenDetail(열기만) — 열린 채 눌러도 닫히지 않게
  detailOpen = false,
  onToggleDetail = null,
  // [상세 조건] 을 아이콘만(오너 2026-09-14) — 시험지 조립 창의 검색 열은 좁아 글자까지 두면 난이도 줄이 두 줄로 꺾인다
  // (화면 1440: 칸 294px · 필요 317px). 이름은 aria-label·title 로 남긴다
  detailIconOnly = false,
  sheet = false,
  disabled = false,
  // (항) → 그 항을 준 상위 행 이름 | null — 「공통」 꼬리표(commonConditions.commonOriginOf)
  commonOf = null,
  // 개념 목록 창이 가리지 않을 칸(두 칸 배치의 결과 칸) — 없으면 창이 화면 전체를 덮는다
  avoidRef = null,
}) {
  // 조건이 없는 줄(제목 줄)도 빈 묶음 하나로 연다 — 고르면 그 줄의 조건 초안이 된다(설계 FE-20260913 §2).
  // 「조건을 하나 이상 고르세요」 경고는 조건을 다 뺀 조건 줄에만 띄운다 — 조건 없는 줄은 작업 칸이 안내한다
  const unset = rawQuery == null;
  const query = unset ? EMPTY_QUERY : rawQuery;
  // 떠 있는 창·판 — seq 로 새로 세운다.
  //   개념 창 { kind: 'concept', g, j, anchor, seq, seed: 연 순간의 식, last: 창이 마지막으로 넘긴 식 | null }
  //   라벨 판 { kind: 'label', g, j, anchor, seq, base: 연 순간의 그 항 } — 판 안 임시 선택을 새로 복사한다
  const [pop, setPop] = useState(null);
  // 축 안에서 몇 번째 항인지 기억하면 앞의 다른 축을 지웠을 때도 대상을 유지할 수 있다.
  const [targetOrdinals, setTargetOrdinals] = useState({});
  const seqRef = useRef(0);
  const rootRef = useRef(null);
  const conceptBtnRef = useRef(null);
  const labelBtnRef = useRef(null);
  const names = useMemo(() => makeNames(dict, labelNames), [dict, labelNames]);
  const g = clampGroup(query, groupIndex);
  const targets = Object.fromEntries(QUICK_FIELDS.map((field) => {
    const indexes = termIndexes(query, g, field);
    const ordinal = Math.min(targetOrdinals[`${g}:${field}`] || 0, Math.max(0, indexes.length - 1));
    return [field, indexes[ordinal]];
  }));
  const anchorRef = useMemo(() => ({ current: pop?.anchor || null }), [pop]);
  const popTerm = pop?.kind === 'label' ? quickTerm(query, pop.g, 'label', pop.j) : null;
  const queryKey = exprKey(query);

  // 고치는 묶음이 바뀌거나 잠기면 닫는다 — 다른 묶음의 선택이 엉뚱한 묶음에 적용되지 않게
  useEffect(() => { setPop(null); }, [g, disabled]);

  // 창·판이 기준으로 삼은 것이 밑에서 바뀌면 닫는다.
  // 라벨 판은 모달이 아니라서 키보드로 판 밖(나무)에 나가 다른 줄을 고를 수 있고, 시험지 조립(W07)은 이 부품을 줄마다 새로 세우지
  // 않는다. 그대로 두면 앞 줄에서 복사한 임시 선택이 새 줄의 항을 덮는다. 같은 줄의 다른 항(난이도 등)이 바뀐 것은 닫지 않는다.
  // 개념 창은 연 순간의 식 위에 고치므로, 창이 넘긴 식이 아닌 것으로 바뀌면(다른 줄 · 원본 되돌림) 닫는다 — 덮어쓰지 않게
  useEffect(() => {
    if (!pop) return;
    if (pop.kind === 'concept') {
      if (queryKey !== exprKey(pop.last ?? pop.seed)) setPop(null);
    } else if (!sameJson(pop.base, popTerm)) setPop(null);
  }, [pop, popTerm, queryKey]);

  const nGroups = groupCount(query);
  const terms = Array.isArray(query.groups?.[g]?.terms) ? query.groups[g].terms : [];
  const conceptVals = quickValues(query, g, 'concept', targets.concept);
  const labelVals = quickValues(query, g, 'label', targets.label);
  const diffVals = quickValues(query, g, 'difficulty', targets.difficulty);
  const noDifficulty = termIndexes(query, g, 'difficulty').length === 0;
  const dupes = QUICK_FIELDS
    .map((f) => ({ field: f, n: termIndexes(query, g, f).length }))
    .filter((d) => d.n > 1);
  const others = otherTermIndexes(query, g, targets).map((j) => ({ j, term: terms[j] }));
  const fromOf = (field) => {
    const t = quickTerm(query, g, field, targets[field]);
    return commonOf && t ? commonOf(t) : null;
  };
  const conceptFrom = fromOf('concept');
  const labelFrom = fromOf('label');
  const diffFrom = fromOf('difficulty');

  const openConcepts = (anchor) => {
    if (disabled) return;
    seqRef.current += 1;
    setPop({
      kind: 'concept', g, j: targets.concept, anchor, seq: seqRef.current, seed: query, last: null,
    });
  };
  const openLabels = (anchor) => {
    if (disabled) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    const j = targets.label;
    const base = quickTerm(query, g, 'label', j);
    setPop((p) => (p && p.kind === 'label' && p.anchor === anchor ? null : {
      kind: 'label', g, j, anchor, base, seq,
    }));
  };
  const closePop = () => setPop(null);

  // 창·판을 연 요소가 사라졌으면(푼 개념의 칩, 셋 이하로 줄어 없어진 「외 N개」) 그 축의 여는 버튼으로 초점을 옮긴다.
  // 사라진 요소에는 초점을 돌려줄 수 없어 초점이 문서 밖(body)으로 떨어진다.
  // 부모가 새 식을 그린 뒤에 봐야 하므로 한 틱 미룬다. 개념 창은 연 요소가 남아 있으면 그리로 돌려준다 —
  // 버튼을 눌러도 초점을 주지 않는 브라우저(Safari)에서는 창이 기억한 「연 순간의 초점」이 body 라서
  const returnFocus = (anchor, kind) => {
    setTimeout(() => {
      const alive = anchor && document.contains(anchor);
      if (alive && kind === 'label') return; // 라벨 판(FloatingPanel)이 연 요소로 돌려준다
      const fallback = kind === 'label' ? labelBtnRef.current : conceptBtnRef.current;
      const el = alive ? anchor : fallback;
      if (el && document.contains(el) && !el.disabled) el.focus();
    }, 0);
  };

  const conceptOf = (id) => {
    const info = names.conceptInfo(id);
    return { id, concept_name: info?.name || `#${id}`, order: info?.order ?? null, concept_type: info?.type ?? null };
  };
  // 칩(ConceptChip)은 누른 요소를 넘겨주지 않는다 — 초점을 돌려줄 칩 몸통을 감싼 자리에서 찾는다
  const chipEl = (id) => rootRef.current?.querySelector(`[data-quick-concept="${String(id)}"] > [role="button"]`) || null;

  const removeConcept = (id) => onChange(setFieldValues(query, g, 'concept', conceptVals.filter((v) => String(v) !== String(id)), { termIndex: targets.concept }));
  const removeLabel = (u) => onChange(setFieldValues(query, g, 'label', labelVals.filter((v) => v !== u), { termIndex: targets.label }));

  // 개념 창이 넘긴 선택 — 연 순간의 식 위에 그 항의 값·유래만 고친다. 방식은 연 항의 것(새 항은 하나라도)
  const changeConcepts = ({ values, source }) => {
    if (pop?.kind !== 'concept') return;
    const next = setFieldValues(pop.seed, pop.g, 'concept', values, { source: source || undefined, termIndex: pop.j });
    const { seq } = pop;
    setPop((p) => (p?.seq === seq ? { ...p, last: next } : p));
    onChange(next);
  };
  // 닫을 때 바뀐 것이 있으면 한 번 — 강의 작업 칸은 초안만(검색은 자동), 시험지 조립은 여기서 검색한다
  const closeConcepts = () => {
    const p = pop;
    setPop(null);
    returnFocus(p?.anchor, 'concept');
    if (p?.last && exprKey(p.last) !== exprKey(p.seed) && onApplySearch) onApplySearch(p.last);
  };
  const applyLabel = ({ values, match }) => {
    const at = pop?.g ?? g;
    returnFocus(pop?.anchor, 'label');
    setPop(null);
    if (onApplySearch) onApplySearch(setFieldValues(query, at, 'label', values, { match, termIndex: pop?.j }));
  };

  const shownConcepts = conceptVals.slice(0, CHIP_MAX);
  const moreConcepts = conceptVals.length - shownConcepts.length;
  const conceptOpen = pop?.kind === 'concept';
  const openDetail = () => { if (onOpenDetail) onOpenDetail(); };
  // [상세 조건] 버튼만 여닫기 — 여닫기를 넘기지 않는 부르는 곳에서는 종전처럼 열기만 한다
  const toggleDetail = () => { if (onToggleDetail) onToggleDetail(); else openDetail(); };
  const targetPicker = (field) => {
    const indexes = termIndexes(query, g, field);
    if (indexes.length < 2) return null;
    return (
      <select
        aria-label={`${FIELDS[field].label} 편집 대상`}
        value={targets[field]}
        disabled={disabled}
        onChange={(e) => {
          setPop(null);
          const ordinal = indexes.indexOf(Number(e.target.value));
          setTargetOrdinals((prev) => ({ ...prev, [`${g}:${field}`]: ordinal }));
        }}
        className={`max-w-full truncate rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 ${ring}`}
      >
        {indexes.map((j) => <option key={j} value={j}>{`조건 ${j + 1} · ${describeTerm(terms[j], names)}`}</option>)}
      </select>
    );
  };
  // 줄 끝의 방식 — 고치는 항의 방식만 바꾼다. 항이 없으면 방식을 둘 곳이 없어 잠근다(첫 칸이 새 항의 방식)
  const matchPicker = (field) => {
    const t = quickTerm(query, g, field, targets[field]);
    const current = t?.match ?? FIELDS[field].matches[0];
    const off = disabled || !t;
    return (
      <div role="group" aria-label={`${ROW_NAME[field]} 엮는 방식`} className="ml-auto inline-flex shrink-0 gap-1">
        {pickableMatches(field).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={current === m}
            disabled={off}
            title={t ? MATCH_HINT[field][m] : `${ROW_NAME[field]}을 고르면 방식을 정할 수 있습니다`}
            onClick={() => { if (t && t.match !== m) onChange(setFieldMatch(query, g, field, m, targets[field])); }}
            className={`${pillCls(current === m)} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {MATCH_LABEL[m]}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div ref={rootRef} className="space-y-2">
      {nGroups > 1 && (
        <div role="group" aria-label="빠른 조건이 고치는 묶음" className="flex flex-wrap items-center gap-1">
          {query.groups.map((_, i) => (
            <button
              // 묶음에는 서버 id 가 없고 자리가 곧 이름(묶음 A·B)이다
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              type="button"
              aria-pressed={i === g}
              title={summarizeGroup(query, i, names)}
              onClick={() => { if (onGroupChange) onGroupChange(i); }}
              className={pillCls(i === g)}
            >
              {groupName(i)}
            </button>
          ))}
          <span className="text-[11px] text-gray-400">묶음 사이는 「또는」 · 빠른 조건은 고른 묶음만 고칩니다</span>
        </div>
      )}

      {terms.length === 0 && !unset && <p className="text-xs text-amber-700">조건을 하나 이상 고르세요</p>}

      <div className={rowCls}>
        <span className={rowLabelCls}>{ROW_NAME.concept}</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {targetPicker('concept')}
          {conceptFrom && <CommonTag from={conceptFrom} />}
          {shownConcepts.map((id) => (
            <span key={id} data-quick-concept={String(id)} className="contents">
              <ConceptChip
                concept={conceptOf(id)}
                onClick={disabled ? undefined : () => openConcepts(chipEl(id))}
                onRemove={disabled ? undefined : () => removeConcept(id)}
              />
            </span>
          ))}
          {moreConcepts > 0 && (
            <button
              type="button"
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={conceptOpen}
              onClick={(e) => openConcepts(e.currentTarget)}
              className={addCls}
            >
              외 {moreConcepts}개
            </button>
          )}
          {/* 두 갈래가 같은 자리의 같은 button 이라 DOM 이 이어진다 — 「+ 매스컨셉」으로 열고 골라 ▾ 로 바뀌어도 초점이 남는다 */}
          {conceptVals.length > 0 ? (
            <button
              ref={conceptBtnRef}
              type="button"
              disabled={disabled}
              aria-label="매스컨셉 고르기"
              aria-haspopup="dialog"
              aria-expanded={conceptOpen}
              onClick={(e) => openConcepts(e.currentTarget)}
              className={handleCls}
            >
              <ChevronDown className="size-3.5" />
            </button>
          ) : (
            <button
              ref={conceptBtnRef}
              type="button"
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={conceptOpen}
              onClick={(e) => openConcepts(e.currentTarget)}
              className={addCls}
            >
              <Plus className="size-3" /> {ROW_NAME.concept}
            </button>
          )}
          {matchPicker('concept')}
        </div>
      </div>

      <div className={rowCls}>
        <span className={rowLabelCls}>{ROW_NAME.label}</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {targetPicker('label')}
          {labelFrom && <CommonTag from={labelFrom} />}
          {labelVals.map((u) => {
            const text = labelNameOf(labelNames, u) || unknownLabelText(u);
            return (
              <span key={u} title={u} className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                <button
                  type="button"
                  disabled={disabled}
                  aria-haspopup="dialog"
                  aria-expanded={pop?.kind === 'label'}
                  onClick={(e) => openLabels(e.currentTarget)}
                  className={`min-w-0 truncate rounded-full text-left ${ring}`}
                >
                  {text}
                </button>
                {!disabled && (
                  <button type="button" onClick={() => removeLabel(u)} aria-label={`${text} 빼기`} className={`rounded-full text-gray-400 hover:text-gray-600 ${ring}`}>
                    <X className="size-3" />
                  </button>
                )}
              </span>
            );
          })}
          <button
            ref={labelBtnRef}
            type="button"
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={pop?.kind === 'label'}
            onClick={(e) => openLabels(e.currentTarget)}
            className={addCls}
          >
            <Plus className="size-3" /> {ROW_NAME.label}
          </button>
          {matchPicker('label')}
        </div>
      </div>

      <div className={rowCls}>
        <span className={rowLabelCls}>난이도</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {targetPicker('difficulty')}
          {diffFrom && <CommonTag from={diffFrom} />}
          {/* 1~5 는 한 덩어리로 줄바꿈한다 — 좁은 화면에서 3 과 4 가 두 줄로 갈라지지 않게(설계 §W08) */}
          <div role="group" aria-label="난이도" className="inline-flex shrink-0 gap-1">
            {[1, 2, 3, 4, 5].map((n) => {
              const on = diffVals.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  disabled={disabled}
                  aria-pressed={on}
                  aria-label={`난이도 ${n}`}
                  onClick={() => onChange(toggleDifficulty(query, g, n, targets.difficulty))}
                  className={diffCls(on ? n : 0)}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            disabled={disabled}
            aria-pressed={noDifficulty}
            onClick={() => { if (!noDifficulty) onChange(clearDifficulty(query, g, targets.difficulty)); }}
            className={`inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${ring} ${
              noDifficulty ? 'bg-gray-100 font-medium text-gray-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            {noDifficulty && <Check className="size-3" />}
            {termIndexes(query, g, 'difficulty').length > 1 ? '이 난이도 조건 제거' : '제한 없음'}
          </button>
          {/* 여닫기(2026-09-14) — 열려 있으면 켜진 모양. 켜짐은 같은 줄 [제한 없음] 의 켜짐과 같은 얼굴(회색 면 · 먹색 글자) */}
          <button
            type="button"
            onClick={toggleDetail}
            aria-expanded={onToggleDetail ? detailOpen : undefined}
            aria-label={detailIconOnly ? '상세 조건' : undefined}
            title={detailIconOnly ? '상세 조건' : undefined}
            className={detailOpen
              ? `inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors ${ring} ml-auto`
              : `${linkCls} ml-auto`}
          >
            <SlidersHorizontal className="size-3.5" />{!detailIconOnly && ' 상세 조건'}
          </button>
        </div>
      </div>

      {others.length > 0 && (
        <div className={rowCls}>
          <span className={rowLabelCls}>그 밖</span>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {others.map(({ j, term }) => {
              const from = commonOf ? commonOf(term) : null;
              const text = `${from ? '공통 · ' : ''}${describeTerm(term, names)}`;
              return (
                <button
                  key={j}
                  type="button"
                  onClick={openDetail}
                  title={`${text} — 상세 조건에서 고칩니다`}
                  className={`max-w-full truncate rounded-full border border-dashed border-gray-300 bg-white px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50 ${ring}`}
                >
                  <MathName text={text} compact />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {dupes.map((d) => (
        <p key={d.field} className="flex flex-wrap items-center gap-1 text-[11px] text-amber-700">
          이 묶음에 {FIELDS[d.field].label} 조건이 {d.n}개 있습니다 — 선택한 조건만 고칩니다 ·
          <button type="button" onClick={openDetail} className={`rounded underline hover:text-amber-800 ${ring}`}>상세 조건</button>
        </p>
      ))}

      {conceptOpen && (
        <ConceptListModal
          key={pop.seq}
          term={quickTerm(pop.seed, pop.g, 'concept', pop.j)}
          dict={dict}
          sheet={sheet}
          avoidRef={avoidRef}
          commonFrom={conceptFrom}
          onChange={changeConcepts}
          onClose={closeConcepts}
        />
      )}
      {pop?.kind === 'label' && (
        <LabelPopover
          key={pop.seq}
          anchorRef={anchorRef}
          term={quickTerm(query, pop.g, 'label', pop.j)}
          labelNames={labelNames}
          onRemember={onRememberLabels}
          sheet={sheet}
          onClose={closePop}
          onApply={applyLabel}
        />
      )}
    </div>
  );
}

export default QuickConditions;
